import { useState, useEffect, useMemo, useRef } from 'react'
import { X, Check } from 'lucide-react'
import { T } from '../theme'
import playerService from '../services/playerService'

/**
 * ShareWhatsAppModal — partage des cartes joueurs en WhatsApp prive
 * individuel (PR #20). S'ouvre apres succes du POST send-all-cards.
 *
 * Props :
 *  - isOpen, onClose
 *  - cards : PlayerCardSummary[] renvoye par send-all-cards
 *      { player_id, player_name, player_first_name, player_phone,
 *        player_email, has_email_sent, share_token, coach_rating,
 *        identity_tag_label, identity_tag_emoji }
 *  - matchInfo : { scoreUs, scoreThem, opponentName, date }
 *      scoreUs/scoreThem sont ORIENTES CLUB par le parent, jamais
 *      score_home/score_away bruts (cf. src/utils/matchResult.js).
 *  - onPlayerUpdated(playerId, { phone }) : remonte l'ajout de numero
 */

const D     = '#0a0908'
const PANEL = '#1a1916'
const GOLD  = '#4F8EF7'
const CREAM = '#f5f2eb'
const MUTED = 'rgba(245,242,235,0.5)'
const RULE  = 'rgba(245,242,235,0.12)'
const GREEN = '#22c55e'
const RED   = '#ef4444'
const WA    = '#25D366'
const MONO  = T.mono
const DISP  = T.display

function isValidPhone(phone) {
  // Mobile francais : 10 chiffres, 0 + [1-9] + 8 chiffres.
  return /^0[1-9]\d{8}$/.test((phone || '').replace(/[\s.]/g, ''))
}

function initials(name) {
  return (name || '?')
    .split(' ').filter(Boolean).slice(0, 2)
    .map(w => w[0].toUpperCase()).join('')
}

function buildMatchLabel(matchInfo) {
  // « Pau FC (0-2) » — le libelle se lit du point de vue du club, donc le
  // premier nombre est le SIEN. matchInfo porte scoreUs/scoreThem deja
  // orientes par le parent (cf. src/utils/matchResult.js) : lire
  // score_home tel quel inversait le score de tout match joue dehors, dans
  // un message envoye aux joueurs et aux familles par WhatsApp.
  const opp = matchInfo?.opponentName || 'le match'
  const us = matchInfo?.scoreUs
  const them = matchInfo?.scoreThem
  return (us != null && them != null) ? `${opp} (${us}-${them})` : opp
}

function buildWhatsAppLink(card, phone, matchLabel) {
  const clean = (phone || '').replace(/\D/g, '').replace(/^0/, '')
  const link = `${window.location.origin}/j/${card.share_token}`
  const tag = card.identity_tag_label
    ? `${card.identity_tag_emoji || ''} ${card.identity_tag_label}`.trim()
    : null
  const note = card.coach_rating != null ? `${card.coach_rating}/10` : null
  const tagLine = [tag, note].filter(Boolean).join(' · ')
  // Filet prénom : player_first_name vient du backend (first_name_or_fallback,
  // k4) mais on re-sécurise côté front pour ne jamais écrire "Salut  👋".
  const firstName =
    (card.player_first_name || '').trim() ||
    (card.player_name || '').trim().split(/\s+/)[0] ||
    'champion'
  const msg =
    `Salut ${firstName} 👋\n\n` +
    `Ta carte du match contre ${matchLabel} est prête !\n\n` +
    (tagLine ? `${tagLine}\n\n` : '') +
    `👀 Vois ta carte ici : ${link}\n\n` +
    `⏳ Lien valable 48h, télécharge-la avant !`
  return `https://wa.me/33${clean}?text=${encodeURIComponent(msg)}`
}

/** Edition inline d'un numero (pattern EmailEdit de SendCardsModal). */
function PhoneEdit({ initial, saving, error, onSave, onCancel }) {
  const [draft, setDraft] = useState(initial || '')
  const ref = useRef(null)
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onCancel() }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onCancel])
  const valid = isValidPhone(draft)
  return (
    <div ref={ref} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="tel"
        inputMode="numeric"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid && !saving) onSave(draft.replace(/[\s.]/g, ''))
          if (e.key === 'Escape') onCancel()
        }}
        placeholder="06 12 34 56 78"
        style={{
          background: '#0a0908', border: `1px solid ${GOLD}`, color: CREAM,
          fontFamily: MONO, fontSize: 12, padding: '6px 9px', borderRadius: 2,
          width: 170, maxWidth: '100%',
        }}
      />
      <button
        type="button"
        onClick={() => { if (valid && !saving) onSave(draft.replace(/[\s.]/g, '')) }}
        disabled={!valid || saving}
        title="Enregistrer"
        style={{
          background: 'transparent', border: `1px solid ${RULE}`, padding: 5,
          cursor: (!valid || saving) ? 'not-allowed' : 'pointer',
        }}
      >
        <Check size={14} color={valid ? GOLD : MUTED} />
      </button>
      <button
        type="button"
        onClick={onCancel}
        title="Annuler"
        style={{ background: 'transparent', border: `1px solid ${RULE}`, padding: 5, cursor: 'pointer' }}
      >
        <X size={14} color={MUTED} />
      </button>
      {saving && <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>...</span>}
      {error && <span style={{ fontFamily: MONO, fontSize: 9, color: RED }}>{error}</span>}
    </div>
  )
}

export default function ShareWhatsAppModal({
  isOpen,
  onClose,
  cards = [],
  matchInfo,
  onPlayerUpdated,
  // T1 — buckets de skip resolus { player_id, name }[]
  skippedAlreadyDownloaded = [],
  skippedNoContact = [],
  skippedNotInMatch = [],
  // T2 — callback renvoi force (depuis SendCardsModal uniquement)
  onForceResend,
  forceResendBusy = false,
  forceResendError = null,
  // T4 — bouton retour (restaure SendCardsModal avec selection intacte)
  onBack,
}) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [phoneOverrides, setPhoneOverrides] = useState({})
  const [sentIds, setSentIds] = useState([])
  const [editingPhoneFor, setEditingPhoneFor] = useState(null)
  const [phoneError, setPhoneError] = useState(null)
  const [savingPhone, setSavingPhone] = useState(false)
  const [chainMode, setChainMode] = useState(false)
  const [chainIndex, setChainIndex] = useState(0)
  const [chainDone, setChainDone] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    setPhoneOverrides({}); setSentIds([]); setEditingPhoneFor(null)
    setPhoneError(null); setSavingPhone(false)
    setChainMode(false); setChainIndex(0); setChainDone(false)
  }, [isOpen])

  const matchLabel = useMemo(() => buildMatchLabel(matchInfo), [matchInfo])
  const phoneOf = (c) => phoneOverrides[c.player_id] ?? c.player_phone ?? null
  const cardsWithPhone = useMemo(
    () => cards.filter(c => isValidPhone(phoneOf(c))),
    [cards, phoneOverrides]
  )

  if (!isOpen) return null

  const hasSkips = skippedAlreadyDownloaded.length > 0 || skippedNoContact.length > 0 || skippedNotInMatch.length > 0

  function openWhatsApp(card) {
    const phone = phoneOf(card)
    if (!isValidPhone(phone)) return
    window.open(buildWhatsAppLink(card, phone, matchLabel), '_blank', 'noopener')
    setSentIds(prev => prev.includes(card.player_id) ? prev : [...prev, card.player_id])
  }

  function startChain() {
    if (cardsWithPhone.length === 0) return
    setChainMode(true); setChainIndex(0); setChainDone(false)
    openWhatsApp(cardsWithPhone[0])
  }
  function nextInChain() {
    const next = chainIndex + 1
    if (next >= cardsWithPhone.length) {
      setChainDone(true); setChainMode(false)
      return
    }
    setChainIndex(next)
    openWhatsApp(cardsWithPhone[next])
  }

  async function handleSavePhone(card, value) {
    const phone = (value || '').replace(/[\s.]/g, '')
    if (!isValidPhone(phone)) { setPhoneError('Numéro invalide (06…)'); return }
    setSavingPhone(true); setPhoneError(null)
    try {
      await playerService.updatePlayer(card.player_id, { phone })
      setPhoneOverrides(prev => ({ ...prev, [card.player_id]: phone }))
      onPlayerUpdated && onPlayerUpdated(card.player_id, { phone })
      setEditingPhoneFor(null)
      // Auto-trigger l'ouverture WhatsApp apres save (brief PR #20).
      setTimeout(() => openWhatsApp({ ...card }), 50)
    } catch (err) {
      setPhoneError(err?.response?.data?.detail || err?.message || 'Erreur sauvegarde')
    } finally {
      setSavingPhone(false)
    }
  }

  const total = cards.length
  const chainCard = chainMode ? cardsWithPhone[chainIndex] : null

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(5,4,4,0.82)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : 16,
      }}
    >
      <div style={{
        background: D, color: CREAM,
        width: '100%', maxWidth: isMobile ? '100%' : 560,
        height: isMobile ? '100dvh' : 'min(88vh, 800px)',
        display: 'flex', flexDirection: 'column',
        border: isMobile ? 'none' : `1px solid ${RULE}`,
        borderTop: isMobile ? 'none' : `2px solid ${WA}`,
      }}>
        {/* Header */}
        <header style={{
          flexShrink: 0, padding: '18px 20px', background: PANEL,
          borderBottom: `1px solid ${RULE}`,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: WA, marginBottom: 6 }}>
              Partage WhatsApp
            </div>
            <h2 style={{
              fontFamily: DISP, fontSize: isMobile ? 19 : 23, textTransform: 'uppercase',
              lineHeight: 1.05, color: CREAM, margin: 0,
            }}>
              {total === 0
                ? (hasSkips ? 'Cartes non envoyées' : 'Aucune carte à envoyer')
                : total === 1
                  ? '🎉 Ta carte est prête'
                  : `🎉 Tes ${total} cartes sont prêtes`}
            </h2>
            {total > 0 && (
              <p style={{ fontFamily: MONO, fontSize: 11, color: MUTED, marginTop: 8 }}>
                Envoie-les à chaque joueur en privé.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{ flexShrink: 0, background: 'none', border: `1px solid ${RULE}`, padding: 6, cursor: 'pointer' }}
          >
            <X size={14} color={MUTED} />
          </button>
        </header>

        {/* Liste joueurs */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '8px 0' }}>
          {cards.map((card, idx) => {
            const phone = phoneOf(card)
            const hasPhone = isValidPhone(phone)
            const editing = editingPhoneFor === card.player_id
            const sent = sentIds.includes(card.player_id)
            const tag = card.identity_tag_label
              ? `${card.identity_tag_emoji || ''} ${card.identity_tag_label}`.trim()
              : null
            return (
              <div
                key={card.player_id}
                style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  padding: '14px 20px',
                  borderBottom: idx < cards.length - 1 ? `1px solid ${RULE}` : 'none',
                  background: chainCard && chainCard.player_id === card.player_id ? 'rgba(37,211,102,0.06)' : 'transparent',
                }}
              >
                <div style={{
                  width: 38, height: 38, flexShrink: 0, borderRadius: 4,
                  background: GOLD, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: MONO, fontSize: 12, fontWeight: 700,
                }}>
                  {initials(card.player_name)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontFamily: 'sans-serif', fontSize: 14, fontWeight: 700, color: CREAM }}>
                      {card.player_name}
                    </strong>
                    {card.has_email_sent && (
                      <span title="Email de secours envoyé" style={{ fontFamily: MONO, fontSize: 10, color: GREEN }}>
                        📧 ✓
                      </span>
                    )}
                  </div>
                  {(tag || card.coach_rating != null) && (
                    <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, marginTop: 3 }}>
                      {[tag, card.coach_rating != null ? `${card.coach_rating}/10` : null].filter(Boolean).join(' · ')}
                    </div>
                  )}

                  <div style={{ marginTop: 8 }}>
                    {editing ? (
                      <PhoneEdit
                        initial={phone || ''}
                        saving={savingPhone}
                        error={phoneError}
                        onSave={(val) => handleSavePhone(card, val)}
                        onCancel={() => { setEditingPhoneFor(null); setPhoneError(null) }}
                      />
                    ) : hasPhone ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: 'rgba(245,242,235,0.7)' }}>
                          {phone}
                        </span>
                        <button
                          type="button"
                          onClick={() => openWhatsApp(card)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '7px 12px', cursor: 'pointer',
                            background: sent ? 'transparent' : WA,
                            color: sent ? WA : '#0a2e16',
                            border: `1px solid ${WA}`,
                            fontFamily: MONO, fontSize: 10, letterSpacing: '.06em',
                            textTransform: 'uppercase', fontWeight: 700,
                          }}
                        >
                          📱 {sent ? 'Renvoyer' : 'Envoyer WhatsApp'}
                        </button>
                        {sent && <Check size={15} color={WA} strokeWidth={3} />}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: '#f59e0b' }}>
                          ⚠️ Pas de téléphone
                        </span>
                        <button
                          type="button"
                          onClick={() => { setPhoneError(null); setEditingPhoneFor(card.player_id) }}
                          style={{
                            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                            color: GOLD, fontFamily: MONO, fontSize: 11,
                            textDecoration: 'underline', textDecorationStyle: 'dotted',
                          }}
                        >
                          📱 Ajouter numéro
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Section skips (T1 + T2) */}
        {hasSkips && (
          <div style={{ borderTop: `1px solid ${RULE}`, padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: MUTED }}>
              ⚠ Non envoyées
            </div>

            {forceResendError && (
              <div style={{
                fontFamily: MONO, fontSize: 11, color: RED,
                background: 'rgba(239,68,68,0.08)', border: `1px solid rgba(239,68,68,0.25)`,
                padding: '8px 10px',
              }}>
                {forceResendError}
              </div>
            )}

            {skippedAlreadyDownloaded.map(({ player_id, name }) => (
              <div key={player_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: CREAM }}>
                  <strong>{name}</strong>{' '}
                  <span style={{ color: MUTED }}>— déjà téléchargée, non renvoyée</span>
                </span>
                {onForceResend && (
                  <button
                    type="button"
                    onClick={() => onForceResend([player_id])}
                    disabled={forceResendBusy}
                    style={{
                      padding: '5px 12px', cursor: forceResendBusy ? 'not-allowed' : 'pointer',
                      background: 'transparent', border: `1px solid ${GOLD}`,
                      fontFamily: MONO, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                      color: forceResendBusy ? MUTED : GOLD,
                    }}
                  >
                    {forceResendBusy ? '...' : 'Renvoyer quand même'}
                  </button>
                )}
              </div>
            ))}

            {skippedNoContact.length > 0 && (
              <div style={{ fontFamily: MONO, fontSize: 11, color: '#f59e0b' }}>
                {skippedNoContact.length} joueur{skippedNoContact.length > 1 ? 's' : ''} sans contact — {skippedNoContact.map(s => s.name).join(', ')}
              </div>
            )}

            {skippedNotInMatch.length > 0 && (
              <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
                {skippedNotInMatch.length} joueur{skippedNotInMatch.length > 1 ? 's' : ''} absent{skippedNotInMatch.length > 1 ? 's' : ''} de la compo — {skippedNotInMatch.map(s => s.name).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Footer : mode chaine + conseil + fermer */}
        <footer style={{
          flexShrink: 0, padding: '14px 20px', background: PANEL,
          borderTop: `1px solid ${RULE}`,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {chainMode && chainCard ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontFamily: MONO, fontSize: 11, color: CREAM }}>
                Envoi {chainIndex + 1}/{cardsWithPhone.length} — <strong>{chainCard.player_name}</strong>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => openWhatsApp(chainCard)}
                  style={{
                    flex: 1, padding: '11px', cursor: 'pointer',
                    background: WA, color: '#0a2e16', border: 'none',
                    fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
                  }}
                >
                  📱 Ouvrir WhatsApp
                </button>
                <button
                  type="button"
                  onClick={nextInChain}
                  style={{
                    flex: 1, padding: '11px', cursor: 'pointer',
                    background: 'transparent', color: CREAM, border: `1px solid ${GOLD}`,
                    fontFamily: MONO, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
                  }}
                >
                  {chainIndex + 1 >= cardsWithPhone.length ? 'Terminer' : 'Suivant →'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={startChain}
              disabled={cardsWithPhone.length === 0}
              style={{
                padding: '12px', width: '100%',
                cursor: cardsWithPhone.length === 0 ? 'not-allowed' : 'pointer',
                background: cardsWithPhone.length === 0 ? 'rgba(79,142,247,0.25)' : GOLD,
                color: cardsWithPhone.length === 0 ? MUTED : D, border: 'none',
                fontFamily: MONO, fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
              }}
            >
              ⚡ Tout envoyer en chaîne ({cardsWithPhone.length} joueur{cardsWithPhone.length > 1 ? 's' : ''})
            </button>
          )}

          {chainDone && (
            <div style={{
              fontFamily: MONO, fontSize: 11, color: GREEN,
              background: 'rgba(34,197,94,0.12)', border: `1px solid ${GREEN}`,
              padding: '8px 10px', textAlign: 'center',
            }}>
              ✅ {cardsWithPhone.length} carte{cardsWithPhone.length > 1 ? 's' : ''} envoyée{cardsWithPhone.length > 1 ? 's' : ''}
            </div>
          )}

          <p style={{ fontFamily: MONO, fontSize: 10, color: MUTED, lineHeight: 1.6, margin: 0 }}>
            💡 Encourage tes joueurs à partager leur carte sur Instagram
            Stories. Plus ils partagent, plus ton club rayonne.
          </p>

          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                padding: '10px', width: '100%',
                background: 'transparent', border: `1px solid ${RULE}`, color: MUTED,
                fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              ← Modifier la sélection
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px', width: '100%',
              background: 'transparent', border: `1px solid ${RULE}`, color: MUTED,
              fontFamily: MONO, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Fermer
          </button>
        </footer>
      </div>
    </div>
  )
}
